import { useEffect } from 'react';

/**
 * Runs the callbacks on window focus / blur, and `onFocus` once on mount unless `ignoreInitial`.
 *
 * Holds no React state on purpose: a setState in the focus handler rendered the tree inside the
 * focus event, and in Firefox that render read the store while a reducer was still running — Redux
 * error #3 (SHOP-WEB-W). RTK Query refetched on that same event at the time; it is now deferred
 * (`installDeferredRtkListeners`), but rendering inside the event is still unsafe.
 */
export default function useFocus(onFocus: () => void, onBlur?: () => void, ignoreInitial?: boolean): void {
  useEffect(() => {
    if (!ignoreInitial) {
      onFocus();

      return () => onBlur && onBlur();
    }
  }, []);

  useEffect(() => {
    const handleBlur = () => onBlur && onBlur();

    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);
}
